import { CloseOutlined, InfoCircleOutlined, UndoOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import type { FC } from 'react';
import { useIntl } from 'react-intl';

import IntlMessages from '@src/util/IntlMessages';

import type { MatrixRemoveExceptionCopyKey } from '../../utils/isRemovableMatrixExceptionCell';
import { matrixRemoveExceptionLabelId } from '../../utils/isRemovableMatrixExceptionCell';

import styles from './styles.module.less';

export type MatrixSelectionBarAction =
  | 'justified_absence'
  | 'unjustified_absence'
  | 'vacation'
  | 'replace'
  | 'remove_exception'
  | 'info';

export type WorkJourneyMatrixSelectionBarProps = {
  cellCount: number;
  userDisplayName: string;
  startDate: string;
  endDate: string;
  /** When false, summary avoids implying a contiguous min–max range. */
  isContiguous?: boolean;
  /**
   * Cover-only / reemplazante rows (`isMember: false`): hide create actions
   * (absence, vacation, replace). Only remove-replacement (+ utils) applies.
   */
  coverOnly?: boolean;
  /** Future selection with an existing removable exception. */
  removeExceptionCopyKey?: MatrixRemoveExceptionCopyKey | null;
  onAction: (action: MatrixSelectionBarAction) => void;
  onClear: () => void;
};

const formatCompactRange = (startDate: string, endDate: string, locale: string): string => {
  const start = new Date(`${startDate}T12:00:00`);
  const end = new Date(`${endDate}T12:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return `${startDate} – ${endDate}`;
  }
  const month = new Intl.DateTimeFormat(locale, { month: 'long' }).format(start);
  if (startDate === endDate) {
    return `${start.getDate()} ${month}`;
  }
  if (start.getMonth() === end.getMonth() && start.getFullYear() === end.getFullYear()) {
    return `${start.getDate()}–${end.getDate()} ${month}`;
  }
  const startLabel = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(
    start,
  );
  const endLabel = new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long' }).format(end);
  return `${startLabel} – ${endLabel}`;
};

export const WorkJourneyMatrixSelectionBar: FC<WorkJourneyMatrixSelectionBarProps> = ({
  cellCount,
  userDisplayName,
  startDate,
  endDate,
  isContiguous = true,
  coverOnly = false,
  removeExceptionCopyKey = null,
  onAction,
  onClear,
}) => {
  const intl = useIntl();
  const locale = intl.locale === 'es' ? 'es-CL' : 'en-US';
  const infoEnabled = cellCount === 1;
  const summary = intl.formatMessage(
    { id: 'workJourney.management.matrix.selectionBar.summary' },
    {
      count: cellCount,
      name: userDisplayName,
    },
  );
  const rangeLabel = isContiguous ? formatCompactRange(startDate, endDate, locale) : null;
  const removeLabelId = removeExceptionCopyKey
    ? matrixRemoveExceptionLabelId(removeExceptionCopyKey)
    : null;

  return (
    <div
      className={styles.bar}
      role="toolbar"
      aria-label={intl.formatMessage({
        id: 'workJourney.management.matrix.selectionActionsTitle',
      })}
    >
      <div className={styles.summary}>
        <div className={styles.summaryPrimary}>{summary}</div>
        {rangeLabel ? <div className={styles.summaryRange}>{rangeLabel}</div> : null}
      </div>

      <div className={styles.actions}>
        {!coverOnly && (
          <>
            <Button
              className={`${styles.chip} ${styles.chipAuj}`}
              onClick={() => onAction('justified_absence')}
            >
              <IntlMessages id="workJourney.management.matrix.selectionBar.justifiedAbsence" />
            </Button>
            <Button
              className={`${styles.chip} ${styles.chipAui}`}
              onClick={() => onAction('unjustified_absence')}
            >
              <IntlMessages id="workJourney.management.matrix.selectionBar.unjustifiedAbsence" />
            </Button>
            <Button
              className={`${styles.chip} ${styles.chipVac}`}
              onClick={() => onAction('vacation')}
            >
              <IntlMessages id="workJourney.management.matrix.selectionBar.vacation" />
            </Button>
            <Button
              className={`${styles.chip} ${styles.chipRem}`}
              onClick={() => onAction('replace')}
            >
              <IntlMessages id="workJourney.management.matrix.selectionBar.replace" />
            </Button>
          </>
        )}
        {removeLabelId ? (
          <Button
            className={`${styles.chip} ${styles.chipRemove}`}
            danger
            onClick={() => onAction('remove_exception')}
          >
            <IntlMessages id={removeLabelId} />
          </Button>
        ) : null}
      </div>

      <div className={styles.utils}>
        <Button
          type="text"
          className={styles.iconBtn}
          icon={<InfoCircleOutlined />}
          disabled={!infoEnabled}
          onClick={() => {
            if (!infoEnabled) return;
            onAction('info');
          }}
          aria-label={intl.formatMessage({
            id: 'workJourney.management.matrix.selectionBar.info',
          })}
        />
        <Button
          type="text"
          className={styles.iconBtn}
          icon={<UndoOutlined />}
          onClick={onClear}
          aria-label={intl.formatMessage({
            id: 'workJourney.management.matrix.selectionBar.clear',
          })}
        >
          <span className={styles.clearLabel}>
            <IntlMessages id="workJourney.management.matrix.selectionBar.clear" />
          </span>
        </Button>
        <Button
          type="text"
          className={styles.iconBtn}
          icon={<CloseOutlined />}
          onClick={onClear}
          aria-label={intl.formatMessage({
            id: 'workJourney.management.matrix.selectionBar.close',
          })}
        />
      </div>
    </div>
  );
};
